/**
 * RequireRole — BOLT-024 route guard.
 *
 * Wraps a route element and gates access by the caller's role. The role is
 * read from the auth store, where it is set by the login flow (BOLT-018).
 *
 * If the caller's role does not match `role`, the component redirects to
 * the lower-role default landing page (`/users`) per DC-015.
 *
 * Usage (composes with `<RequireAuth>` and `<AppShell>`):
 *
 *   <Route
 *     path="/admin/orgs"
 *     element={
 *       <RequireAuth>
 *         <RequireRole role="super_admin">
 *           <AppShell>
 *             <OrgSearchPage />
 *           </AppShell>
 *         </RequireRole>
 *       </RequireAuth>
 *     }
 *   />
 *
 * Note: client-side role checks are advisory — the API enforces the role
 * authoritatively. This guard only avoids mounting routes that the user
 * has no business seeing.
 */
import type { ReactNode } from 'react';
import { Navigate } from 'react-router';
import { useAuthStore, type UserRole } from '@/store/auth.js';

interface RequireRoleProps {
  role: UserRole;
  children: ReactNode;
}

export function RequireRole({ role, children }: RequireRoleProps) {
  const userRole = useAuthStore((s) => s.role);

  // No role yet (e.g. token without role claim) → redirect to safe default.
  // This is intentionally strict: missing role is treated as insufficient.
  if (userRole !== role) {
    return <Navigate to="/users" replace />;
  }

  return <>{children}</>;
}
